import { useEffect, useRef } from "react";
import { useGamification } from "@/hooks/use-gamification";
import { useGamificationToastStore, type GamificationToast } from "@lyder-talk/core/stores/gamification-toast";
import { useMascotMood, type UseMascotMoodResult } from "./useMascotMood";

/**
 * Home/dashboard mascot mood.
 *
 * Base is always "idle" (Home stays welcoming — streak level does not make
 * Home "sad"). The hook subscribes to the gamification toast store and fires
 * celebratory/reaction pulses when new gamification events arrive, so the
 * hero, streak widget, and streak modal mascots all celebrate in sync.
 */
export const useHomeMascotMood = (): UseMascotMoodResult => {
    const mascot = useMascotMood({ baseMood: "idle" });
    // Touch the gamification hook so streak/level data is loaded in context;
    // base stays idle regardless of value.
    useGamification();

    const toasts = useGamificationToastStore((s) => s.toasts);
    const lastToastIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (toasts.length === 0) {
            lastToastIdRef.current = null;
            return;
        }
        const latest = toasts[toasts.length - 1] as GamificationToast;
        if (latest.id === lastToastIdRef.current) return;
        lastToastIdRef.current = latest.id;

        switch (latest.type) {
            case "milestone-achieved":
            case "level-up":
                mascot.pulse("perfect");
                break;
            case "challenge-complete":
                mascot.pulse("correct");
                break;
            case "freeze-used":
                mascot.pulse("wrong", 1800);
                break;
            case "streak": {
                const data = latest.data as { isNewRecord?: boolean };
                if (data?.isNewRecord) mascot.pulse("perfect");
                else mascot.pulse("correct");
                break;
            }
            default:
                break;
        }
    }, [toasts, mascot]);

    return mascot;
};